/**
 * Post-section survey feedback document schemas and Firebase paths.
 *
 * Defines the Firestore schema for AI-generated feedback on section leader
 * performance. Shared between the webapp and functions packages.
 */

import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';
import type {SectionId, SectionWeekId} from '../../../section/sectionApi';
import type {CourseId} from '../../../types/branded';
import {
  getSectionAnalysisWeekChildCollectionPath,
  SECTION_ANALYSIS_SL_AI_FEEDBACK_SUBCOLLECTION,
} from './sectionAnalysisFirestorePaths';

// ============================================================================
// Idempotency Window
// ============================================================================

/**
 * Window (in milliseconds) within which a `claiming` doc is treated as still
 * representing an in-flight generation. Used by:
 *  - the server transaction gate to dedupe duplicate idempotency-key writes,
 *  - the client `fetchFeedbackForWeek` to suppress auto-regeneration while
 *    another invocation is still running.
 *
 * Aligned with the cloud function's 5-minute `timeoutSeconds` so a stuck
 * generation cannot remain "in flight" longer than the function itself can
 * possibly run.
 */
export const SL_FEEDBACK_IDEMPOTENCY_WINDOW_MS = 5 * 60 * 1000;

// ============================================================================
// Feedback Provider
// ============================================================================

/** All supported AI providers for generating feedback. */
export const ALL_FEEDBACK_PROVIDERS = ['openai', 'claude', 'gemini'] as const;

/** Schema for feedback provider selection. */
export const FeedbackProviderSchema = z.enum(ALL_FEEDBACK_PROVIDERS);

/** AI provider to use for generating feedback. */
export type FeedbackProvider = z.infer<typeof FeedbackProviderSchema>;

// ============================================================================
// Structured Feedback Schemas
// ============================================================================

/** Schema for a structured feedback moment reference. */
export const FeedbackMomentSchema = z.object({
  /** Start time from the beginning of the recording in HH:MM:SS. */
  start: z.string(),
  /** End time from the beginning of the recording in HH:MM:SS. */
  end: z.string(),
});

/** Schema for structured feedback with a focus moment from the transcript. */
export const StructuredFeedbackSchema = z.object({
  /** Opening praise highlighting what the section leader did well this week. */
  openingPraise: z.string(),
  /** A single moment in the transcript to focus on and learn from. */
  moment: FeedbackMomentSchema.optional(),
  /** Feedback for the specific moment - what happened and how to improve. */
  momentFeedback: z.string().optional(),
  /** A goal for the TA to incorporate in their next section. */
  nextGoal: z.string(),
});

/** Structured feedback with a focus moment from the transcript. */
export type StructuredFeedback = z.infer<typeof StructuredFeedbackSchema>;

// ============================================================================
// Feedback Document Schema
// ============================================================================

/**
 * Status values for SL feedback documents.
 *
 * - `'succeeded'` (or `undefined`): a successful AI-generated feedback doc.
 *   Carries `feedback`, `generatedAt`, and `model`.
 * - `'claiming'`: an in-flight placeholder ("claim doc") written at the start
 *   of a generation attempt to gate concurrent callers with the same
 *   `idempotencyKey`. Carries `claimedAt` and `idempotencyKey` but no
 *   `feedback` yet — if the call succeeds, the same document is overwritten
 *   with the final feedback; if it fails, the doc is rewritten as a
 *   `'failed'` sentinel. Clients MUST NOT treat a claim doc as a successful
 *   result; check for `feedback != null` rather than `status !== 'failed'`.
 * - `'failed'`: a sentinel doc written when generation failed; contains no
 *   feedback content. The client uses this to distinguish "never tried" from
 *   "tried and failed" so it can show a manual "Try again" button instead of
 *   auto-retrying on every page load.
 */
export const FeedbackStatusSchema = z.enum(['succeeded', 'claiming', 'failed']);

/** Status of a feedback document (absent = succeeded for legacy docs). */
export type FeedbackStatus = z.infer<typeof FeedbackStatusSchema>;

/**
 * Schema for a post-section survey SL feedback Firestore document.
 *
 * Uses `.loose()` to allow additional Firestore metadata fields.
 *
 * Field presence by status:
 * - success (`status === 'succeeded'` or absent): `feedback`, `generatedAt`,
 *   `model` present; `failedAt`/`claimedAt` absent.
 * - claiming (`status === 'claiming'`): `claimedAt` and `idempotencyKey`
 *   present; `feedback`/`generatedAt`/`model`/`failedAt` absent.
 * - failed (`status === 'failed'`): `failedAt` and `failureReason` present;
 *   `feedback`/`generatedAt`/`model`/`claimedAt` absent.
 *
 * Denormalized path fields (`courseId`, `sectionId`, `sectionWeek`) are
 * optional for back-compat with documents written before the denorm migration.
 * New writes always stamp all three. Use these for `collectionGroup` queries
 * and BigQuery exports; do not rely on them being present on legacy docs.
 */
export const SlSectionFeedbackDocumentSchema = z
  .object({
    /** The AI-generated feedback (absent on claim / failed-sentinel docs). */
    feedback: StructuredFeedbackSchema.optional(),
    /** Timestamp when the feedback was generated (present on success docs only). */
    generatedAt: FirestoreTimestampSchema.optional(),
    /** The model used to generate the feedback (present on success docs only). */
    model: z.string().optional(),
    /**
     * Document status. Absent or `'succeeded'` means a real feedback doc;
     * `'claiming'` means an in-flight placeholder (no feedback yet); `'failed'`
     * means generation failed and the client should not auto-retry.
     */
    status: FeedbackStatusSchema.optional(),
    /** Timestamp when generation failed (present on failed-sentinel docs). */
    failedAt: FirestoreTimestampSchema.optional(),
    /** Short human-readable reason the generation failed. */
    failureReason: z.string().optional(),
    /**
     * Timestamp when the claim doc was written at the start of an in-flight
     * generation attempt (present only on `status === 'claiming'` docs).
     */
    claimedAt: FirestoreTimestampSchema.optional(),
    /**
     * Idempotency key supplied by the client on the request that created this
     * doc. Used to gate duplicate writes: if a doc with the same key already
     * exists for `(courseId, sectionId, sectionWeek)` the server returns early
     * instead of creating a new doc.
     */
    idempotencyKey: z.string().optional(),
    /**
     * Denormalized course identifier. Optional for back-compat with documents
     * written before the denorm migration; all new writes include this field.
     * Enables efficient `collectionGroup` queries filtered by course.
     */
    courseId: z.string().optional(),
    /**
     * Denormalized section identifier. Optional for back-compat with documents
     * written before the denorm migration; all new writes include this field.
     */
    sectionId: z.string().optional(),
    /**
     * Denormalized week number. Optional for back-compat with documents written
     * before the denorm migration; all new writes include this field.
     */
    sectionWeek: z.number().optional(),
    /**
     * Firebase Auth uid of the caller who triggered this generation attempt
     * (typically the SL whose section is being analyzed, but may be a covering
     * SL, TA, or admin generating on behalf). Optional for back-compat with
     * documents written before this field was tracked; cannot be backfilled
     * since the path does not encode the caller. Stored for audit and
     * future per-user generation analytics.
     */
    triggeredByUserId: z.string().optional(),
  })
  .loose();

/**
 * AI-generated feedback for a post-section survey.
 *
 * The `generatedAt` field accepts Timestamp from either `firebase/firestore`
 * or `firebase-admin/firestore`, as they are structurally compatible.
 */
export type SlSectionFeedbackDocument = z.infer<
  typeof SlSectionFeedbackDocumentSchema
>;

// ============================================================================
// Callable Function Definitions
// ============================================================================

/** Schema for a survey question included in a feedback request. */
export const PostSectionSurveyQuestionForFeedbackSchema = z.object({
  id: z.string(),
  questionText: z.string(),
});

/** A survey question included in a feedback generation request. */
export type PostSectionSurveyQuestionForFeedback = z.infer<
  typeof PostSectionSurveyQuestionForFeedbackSchema
>;

/**
 * Schema for a request to generate AI feedback for a post-section survey.
 *
 * For compile-time branded type safety, use the
 * {@link GenerateSlSectionFeedbackRequest} interface.
 */
export const GenerateSlSectionFeedbackRequestSchema = z.object({
  courseId: z.string(),
  sectionId: z.string(),
  sectionWeek: z.number(),
  responseLanguage: z.string(),
  questions: z.array(PostSectionSurveyQuestionForFeedbackSchema),
  provider: FeedbackProviderSchema.optional(),
  /**
   * Optional idempotency key generated by the client.
   * If a doc with the same key already exists for (courseId, sectionId,
   * sectionWeek) within a 5-minute window, the server returns early rather
   * than creating a duplicate.
   */
  idempotencyKey: z.string().optional(),
});

/**
 * Request to generate AI feedback for a post-section survey.
 *
 * Uses branded types (not z.infer) for compile-time type safety.
 * For runtime validation, use {@link GenerateSlSectionFeedbackRequestSchema}.
 */
export interface GenerateSlSectionFeedbackRequest {
  /** The course ID for the section. */
  courseId: CourseId;
  /** The section ID to generate feedback for. */
  sectionId: SectionId;
  /** The week number of the section. */
  sectionWeek: SectionWeekId;
  /** The language code to respond in. */
  responseLanguage: string;
  /** The question IDs and question texts for the survey. */
  questions: PostSectionSurveyQuestionForFeedback[];
  /** The AI provider to use. Defaults to 'openai' if not specified. */
  provider?: FeedbackProvider;
  /**
   * Optional client-generated idempotency key (e.g. `crypto.randomUUID()`).
   * Pass a new key each time the user explicitly requests a retry. The server
   * transaction-gates writes: if a doc with this key already exists for
   * (courseId, sectionId, sectionWeek) within 5 minutes, it returns early.
   */
  idempotencyKey?: string;
}

/**
 * The AI-generated feedback is written to the database at:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slAiFeedback`
 */
export type GenerateSlSectionFeedbackResponse = undefined;

// ============================================================================
// Paths
// ============================================================================

/**
 * Constructs the path to the SL feedback collection for a post-section survey.
 * Collection path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slAiFeedback`
 */
export function getSlSectionFeedbackCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return getSectionAnalysisWeekChildCollectionPath({
    courseId,
    sectionId,
    sectionWeek,
    leafSubcollection: SECTION_ANALYSIS_SL_AI_FEEDBACK_SUBCOLLECTION,
  });
}

/**
 * Constructs the path to a specific SL feedback document.
 * Document path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slAiFeedback/{feedbackId}`
 */
export function getSlSectionFeedbackPath({
  courseId,
  sectionId,
  sectionWeek,
  feedbackId,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  feedbackId: string;
}): string {
  return `${getSlSectionFeedbackCollectionPath({courseId, sectionId, sectionWeek})}/${feedbackId}`;
}
